import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import ReturnLinesEditor, { returnLinesTotal, toReturnLineRequests, useReturnLookups, validateReturnLines } from "./ReturnLinesEditor";

const money = (value) =>
  value === null || value === undefined || value === "" ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const right = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

const Detail = ({ label, children }) => (
  <div>
    <div className="page-subtitle">{label}</div>
    <div style={{ fontWeight: 600 }}>{children}</div>
  </div>
);

/**
 * Full cancellation / full return of one sales invoice. The backend marks the invoice CANCELLED or RETURNED, raises a credit
 * note for whatever is left on it and cancels its e-way bill and e-invoice.
 *
 * `invoice` needs `id` and `invoiceNumber`; the invoice's details and line items are loaded here so the user sees exactly
 * what is being reversed, wherever the dialog is opened from.
 *
 * Other products taken back at the same time (not on this bill — mostly expired stock) can be added with the warehouse each
 * goes into; they are credited to the customer's account balance on a customer credit note.
 *
 * With `allowPartial` (sales invoices page) the user can instead pick some of the bill's products and enter the quantities coming
 * back: those go into stock, a credit note for them lowers the bill, and the invoice stays open.
 */
const FullReversalModal = ({ invoice, onClose, onDone, allowPartial = false }) => {
  const [mode, setMode] = useState(allowPartial ? "PARTIAL" : "RETURN");
  const [partialLines, setPartialLines] = useState([{ invoiceItemId: "", quantity: "", warehouseId: "" }]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [extraLines, setExtraLines] = useState([]);
  const [showExtra, setShowExtra] = useState(false);
  const { products, warehouses } = useReturnLookups();

  useEffect(() => {
    let cancelled = false;
    setDetails(null);
    setLoadError("");
    api
      .get(`/api/v1/sales-invoices/${invoice.id}`)
      .then((data) => {
        if (!cancelled) setDetails(data);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || "Could not load the invoice details");
      });
    return () => {
      cancelled = true;
    };
  }, [invoice.id]);

  const setPartialLine = (index, field, value) =>
    setPartialLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));

  const submitPartial = async () => {
    const lines = partialLines.filter((line) => line.invoiceItemId || line.quantity);
    if (!lines.length || lines.some((line) => !line.invoiceItemId || !(Number(line.quantity) > 0))) {
      toast.error("Pick a product and enter the quantity for every line");
      return;
    }
    for (const line of lines) {
      const item = (details?.items || []).find((i) => String(i.id) === String(line.invoiceItemId));
      const left = Number(item?.quantity || 0) - Number(item?.returnedQuantity || 0);
      if (Number(line.quantity) > left) {
        toast.error(`${item?.productName || "Product"}: only ${qty(left)} left to return`);
        return;
      }
    }
    setSaving(true);
    try {
      const result = await api.post(`/api/v1/sales-invoices/${invoice.id}/partial-return`, {
        remarks: remarks.trim() || null,
        items: lines.map((line) => ({
          invoiceItemId: Number(line.invoiceItemId),
          quantity: Number(line.quantity),
          warehouseId: line.warehouseId ? Number(line.warehouseId) : null,
        })),
      });
      toast.success(result?.message || "Goods returned");
      onDone?.(result);
      onClose();
    } catch (error) {
      console.error("Partial return failed", error);
      toast.error(error.message || "Could not return the goods");
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "PARTIAL") {
      await submitPartial();
      return;
    }
    if (extraLines.length) {
      const problem = validateReturnLines(extraLines);
      if (problem) {
        toast.error(`Other products: ${problem}`);
        return;
      }
    }
    setSaving(true);
    try {
      const result = await api.post(`/api/v1/sales-invoices/${invoice.id}/full-reversal`, {
        mode,
        remarks: remarks.trim() || null,
        extraItems: extraLines.length ? toReturnLineRequests(extraLines).map(({ productId, quantity, unitPrice, warehouseId }) => ({
          productId,
          quantity,
          unitPrice,
          warehouseId,
        })) : null,
      });
      toast.success(result?.message || "Invoice reversed");
      onDone?.(result);
      onClose();
    } catch (error) {
      console.error("Full cancellation / return failed", error);
      toast.error(error.message || "Could not cancel / return the invoice");
    } finally {
      setSaving(false);
    }
  };

  const shown = details || invoice;
  const items = details?.items || [];
  // what the credit note will cover: everything not already returned
  const remaining = (item) => Math.max(0, Number(item.quantity || 0) - Number(item.returnedQuantity || 0));

  return (
    <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: "980px" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">
            {allowPartial ? "Return" : "Full Return / Cancellation"} — {invoice.invoiceNumber}
          </h3>
          <button className="close-btn" onClick={onClose}>
            Close
          </button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px 20px" }}>
            <Detail label="Customer">{shown.customerName || "-"}</Detail>
            <Detail label="Invoice Date">{shown.invoiceDate || "-"}</Detail>
            <Detail label="Status">{shown.status || "-"}</Detail>
            <Detail label="Invoice Amount">₹{money(shown.totalAmount)}</Detail>
            <Detail label="Tax">₹{money(shown.totalTax)}</Detail>
            <Detail label="Collected">₹{money(shown.amountCollected)}</Detail>
            <Detail label="Balance">₹{money(shown.balanceAmount)}</Detail>
          </div>

          <h4 style={{ marginTop: "18px", marginBottom: "6px", fontWeight: 600 }}>Items on this invoice</h4>
          {loadError ? (
            <div className="page-subtitle" style={{ color: "var(--color-danger)" }}>{loadError}</div>
          ) : !details ? (
            <div className="page-subtitle">Loading items...</div>
          ) : items.length === 0 ? (
            <div className="page-subtitle">
              This invoice was entered without line items; the credit note will be for its outstanding balance.
            </div>
          ) : (
            <div className="table-wrapper" style={{ marginTop: 0, maxHeight: "320px", overflowY: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th style={{ textAlign: "left" }}>Item</th>
                    <th style={right}>Qty</th>
                    <th style={right}>Delivered</th>
                    <th style={right}>Already Returned</th>
                    <th style={right}>Unit Price</th>
                    <th style={right}>Discount</th>
                    <th style={right}>Tax</th>
                    <th style={right}>Line Total</th>
                    <th style={right} title="Quantity not yet returned — what this cancellation / return will credit">
                      Qty To Be Credited
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.skuCode || "-"}</td>
                      <td style={{ textAlign: "left" }}>{item.productName || `Product #${item.productId}`}</td>
                      <td style={right}>
                        {qty(item.quantity)} {item.uom || ""}
                      </td>
                      <td style={right}>{qty(item.deliveredQuantity)}</td>
                      <td style={right}>{qty(item.returnedQuantity)}</td>
                      <td style={right}>{money(item.unitPrice)}</td>
                      <td style={right}>{money(item.discountAmount)}</td>
                      <td style={right}>{money(item.taxAmount)}</td>
                      <td style={right}>{money(item.lineTotal)}</td>
                      <td style={right}>{qty(remaining(item))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={8} style={{ textAlign: "right", fontWeight: 700 }}>
                      Total
                    </td>
                    <td style={{ ...right, fontWeight: 700 }}>{money(items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0))}</td>
                    <td style={{ ...right, fontWeight: 700 }}>{qty(items.reduce((sum, item) => sum + remaining(item), 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {mode === "PARTIAL" ? (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 6, fontWeight: 600 }}>Products coming back</h4>
              {partialLines.map((line, index) => (
                <div key={index} style={{ display: "grid", gridTemplateColumns: "minmax(220px, 3fr) 110px minmax(150px, 2fr) 32px", gap: 8, marginBottom: 6 }}>
                  <select
                    value={line.invoiceItemId}
                    onChange={(e) => setPartialLine(index, "invoiceItemId", e.target.value)}
                    aria-label="Product"
                  >
                    <option value="">Select product</option>
                    {items
                      .filter((item) => remaining(item) > 0)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.productName || `Product #${item.productId}`} ({qty(remaining(item))} left)
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={line.quantity}
                    placeholder="Qty"
                    aria-label="Quantity"
                    onChange={(e) => setPartialLine(index, "quantity", e.target.value)}
                  />
                  <select value={line.warehouseId} onChange={(e) => setPartialLine(index, "warehouseId", e.target.value)} aria-label="Warehouse">
                    <option value="">Default warehouse</option>
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                  {partialLines.length > 1 ? (
                    <button
                      type="button"
                      className="close-btn"
                      style={{ padding: "2px 6px" }}
                      onClick={() => setPartialLines((prev) => prev.filter((_, i) => i !== index))}
                      aria-label="Remove line"
                    >
                      &times;
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="secondary"
                style={{ padding: "5px 12px" }}
                onClick={() => setPartialLines((prev) => [...prev, { invoiceItemId: "", quantity: "", warehouseId: "" }])}
              >
                + Add Product
              </button>
            </div>
          ) : (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <input type="checkbox" checked={showExtra} onChange={(e) => {
                setShowExtra(e.target.checked);
                if (!e.target.checked) setExtraLines([]);
              }} />
              Also take back other products not on this bill (e.g. expired stock)
            </label>
            {showExtra ? (
              <div style={{ marginTop: 8 }}>
                <div className="page-subtitle" style={{ marginBottom: 6 }}>
                  These go into the warehouse picked and their value is credited to the customer's account balance (customer credit note),
                  to be adjusted against bills later.
                </div>
                <ReturnLinesEditor
                  lines={extraLines}
                  onChange={setExtraLines}
                  products={products}
                  warehouses={warehouses}
                  allowInvoiceItems={false}
                  otherLabel="Add product"
                />
                {extraLines.length ? (
                  <div className="page-subtitle" style={{ marginTop: 6 }}>
                    Credit to customer's account: about ₹{returnLinesTotal(extraLines).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label>What happened?</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                {allowPartial ? <option value="PARTIAL">Partial return (some goods came back)</option> : null}
                <option value="RETURN">Full return (all goods came back)</option>
                <option value="CANCEL">Full cancellation (invoice is cancelled)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Remarks</label>
              <input value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={500} placeholder="Reason (optional)" />
            </div>
          </div>
          <div className="page-subtitle" style={{ marginTop: 8 }}>
            {mode === "PARTIAL"
              ? "The goods go into stock and a credit note for them lowers the bill. The invoice stays open."
              : "A credit note is created for the whole remaining invoice, and its e-way bill and e-invoice are cancelled. This cannot be undone."}
          </div>
          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>
              Keep invoice
            </button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? "Processing..." : mode === "PARTIAL" ? "Confirm partial return" : mode === "RETURN" ? "Confirm full return" : "Confirm full cancellation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FullReversalModal;
